import type { HugeNumber } from "@/shared/numbers";
import { hugeZero } from "@/shared/numbers";
import type {
  MHovState,
  NHovBranchExperienceByBranch,
  NHovBranchId,
  NHovEffectBoundaryCaps,
  NHovState,
  OHovRunRecords,
  OHovState,
} from "../types";

export const HOVER_RESET_PLAN_CONTRACT_VERSION = "hover-reset-plans-v1";
import type { MHovExecuteOutcome } from "../mHov/mHovLogic";
import { advanceOHovRunRecords } from "../oHov/oHovLogic";

// Shared reset-layer plan definitions live in core because they describe
// authoritative state scopes across MHov, NHov, OHov, and future reset layers.
export type HoverResetLayer = "mHov" | "nHov" | "oHov";

// The "outside" family currently covers OHov; deeper Hov progress beyond the
// 1.8e308 wall stays within OHov high-target progression rather than a separate
// layer. PHov should add a distinct family because it needs pre-PHov cleanup,
// deepHov grant, and Tim unlock contracts. A genuinely separate outside-family
// reset layer would only be designed later under its own name and contracts.
export type HoverResetFamily = "mHov" | "nHov" | "outside";

export type HoverResetPresentationIntent =
  | "mHov-cell-merge"
  | "nHov-branch-swap"
  | "outside-reset-burst";

export interface HoverResetProgressScope {
  readonly hovResource: "reset-to-zero" | "preserve";
  readonly hiddenHovGainBonus: "reset-to-zero" | "preserve";
  readonly purchasedHovGainUpgrades: "reset-to-zero" | "preserve";
  readonly hovUpgradeCost: "reset-to-base" | "preserve";
  readonly timUpgradeCost: "reset-to-base" | "preserve";
  readonly hovTargetGrid: "reset-to-default" | "preserve";
  readonly hovGridThemeHue: "clear" | "preserve" | "set-from-nHov-branch";
  readonly mHovState: "grant" | "reset-to-default" | "preserve";
  readonly nHovState: "grant" | "reset-to-default" | "preserve";
  readonly secretProgressTimers: "reset-to-zero" | "preserve";
  readonly hovAmplifier: "reset-gauge-and-ap-preserve-enhancement" | "preserve";
}

export interface HoverResetPreserveScope {
  readonly timResource: "preserve";
  readonly purchasedTimGainUpgrades: "preserve";
  readonly timTargetGrid: "preserve";
  readonly hiddenTimGainBonus: "preserve";
  readonly hiddenHovTimProgress: "preserve";
  readonly hovTimBalance: "preserve";
  readonly mpmState: "preserve";
  readonly paymentAndEntitlements: "preserve";
  readonly cumulativeStats: "preserve";
  readonly settings: "preserve";
  readonly oHovState: "grant" | "preserve";
}

export interface HoverResetDerivedSyncIntent {
  readonly mainResourceGains: boolean;
  readonly thresholdUnlocks: boolean;
  readonly hovTargetRootCell: "synced-by-grid-reset" | "preserve";
  readonly timTargetRootCell: "preserve";
}

export interface HoverResetRuntimeEffects {
  readonly presentation: HoverResetPresentationIntent;
  readonly outsideResetSerial: "advance" | "preserve";
}

interface HoverResetPlanBase {
  readonly layer: HoverResetLayer;
  readonly family: HoverResetFamily;
  readonly progress: HoverResetProgressScope;
  readonly preserve: HoverResetPreserveScope;
  readonly derivedSync: HoverResetDerivedSyncIntent;
  readonly runtimeEffects: HoverResetRuntimeEffects;
}

export interface ResolveMHovResetPlanInput {
  readonly rewardBaseHov: HugeNumber;
  readonly outcome: MHovExecuteOutcome;
}

export interface MHovResetPlan extends HoverResetPlanBase {
  readonly layer: "mHov";
  readonly family: "mHov";
  readonly mHovGrant: {
    readonly nextState: MHovState;
    readonly rewardBaseHov: HugeNumber;
    readonly rewardMultiplier: HugeNumber;
  };
}

export interface ResolveNHovResetPlanInput {
  readonly branchId: NHovBranchId;
  readonly selectedThemeHue: number;
  readonly nextCount: number;
  readonly nextBranchExperienceByBranch: NHovBranchExperienceByBranch;
  readonly nextEffectBoundaryCaps: NHovEffectBoundaryCaps;
  readonly nextContinuousEffectProgressByBranch?: NonNullable<
    NHovState["continuousEffectProgressByBranch"]
  >;
}

export interface NHovResetPlan extends HoverResetPlanBase {
  readonly layer: "nHov";
  readonly family: "nHov";
  readonly nHovGrant: {
    readonly branchId: NHovBranchId;
    readonly selectedThemeHue: number;
    readonly nextCount: number;
    readonly nextBranchExperienceByBranch: NHovBranchExperienceByBranch;
    readonly nextEffectBoundaryCaps: NHovEffectBoundaryCaps;
    readonly nextContinuousEffectProgressByBranch: NonNullable<
      NHovState["continuousEffectProgressByBranch"]
    >;
  };
}

export interface ResolveOHovResetPlanInput {
  readonly currentOHovState: OHovState;
  readonly nowMs: number;
  // Game-time elapsed for the in-flight run, supplied by the model. Used to
  // populate `fastestRunGameDurationMs` so admin speed cannot trivialize the
  // "fast/quick OHov" achievements or the stats screen's fastest-OHov record.
  readonly currentRunGameElapsedMs: number;
  readonly powerGain: HugeNumber;
  readonly hovCoreGain: HugeNumber;
}

export interface OHovResetPlan extends HoverResetPlanBase {
  readonly layer: "oHov";
  readonly family: "outside";
  readonly oHovGrant: {
    readonly countDelta: 1;
    readonly powerGain: HugeNumber;
    readonly hovCoreGain: HugeNumber;
    readonly nextRunRecords: OHovRunRecords;
  };
}

export type HoverResetPlan = MHovResetPlan | NHovResetPlan | OHovResetPlan;

const COMMON_PRESERVE_SCOPE: Omit<HoverResetPreserveScope, "oHovState"> = {
  timResource: "preserve",
  purchasedTimGainUpgrades: "preserve",
  timTargetGrid: "preserve",
  hiddenTimGainBonus: "preserve",
  hiddenHovTimProgress: "preserve",
  hovTimBalance: "preserve",
  mpmState: "preserve",
  paymentAndEntitlements: "preserve",
  cumulativeStats: "preserve",
  settings: "preserve",
};

const RESET_HOV_TARGET_DERIVED_SYNC: HoverResetDerivedSyncIntent = {
  mainResourceGains: true,
  thresholdUnlocks: true,
  hovTargetRootCell: "synced-by-grid-reset",
  timTargetRootCell: "preserve",
};

export function resolveMHovResetPlan(
  input: ResolveMHovResetPlanInput,
): MHovResetPlan {
  return {
    layer: "mHov",
    family: "mHov",
    progress: {
      hovResource: "reset-to-zero",
      hiddenHovGainBonus: "preserve",
      purchasedHovGainUpgrades: "reset-to-zero",
      hovUpgradeCost: "reset-to-base",
      timUpgradeCost: "reset-to-base",
      hovTargetGrid: "reset-to-default",
      hovGridThemeHue: "preserve",
      mHovState: "grant",
      nHovState: "preserve",
      secretProgressTimers: "preserve",
      hovAmplifier: "preserve",
    },
    preserve: {
      ...COMMON_PRESERVE_SCOPE,
      oHovState: "preserve",
    },
    derivedSync: RESET_HOV_TARGET_DERIVED_SYNC,
    runtimeEffects: {
      presentation: "mHov-cell-merge",
      outsideResetSerial: "preserve",
    },
    mHovGrant: {
      nextState: {
        count: input.outcome.newCount,
        hgMultiplier: input.outcome.newHgMultiplier,
        currentLoopHovEarned: hugeZero(),
        maxHovSinceLastMHov: hugeZero(),
        lastRewardBaseHov: input.rewardBaseHov,
      },
      rewardBaseHov: input.rewardBaseHov,
      rewardMultiplier: input.outcome.rewardMultiplier,
    },
  };
}

export function resolveNHovResetPlan(
  input: ResolveNHovResetPlanInput,
): NHovResetPlan {
  return {
    layer: "nHov",
    family: "nHov",
    progress: {
      hovResource: "reset-to-zero",
      hiddenHovGainBonus: "reset-to-zero",
      purchasedHovGainUpgrades: "reset-to-zero",
      hovUpgradeCost: "reset-to-base",
      timUpgradeCost: "preserve",
      hovTargetGrid: "reset-to-default",
      hovGridThemeHue: "set-from-nHov-branch",
      mHovState: "reset-to-default",
      nHovState: "grant",
      secretProgressTimers: "reset-to-zero",
      hovAmplifier: "preserve",
    },
    preserve: {
      ...COMMON_PRESERVE_SCOPE,
      oHovState: "preserve",
    },
    derivedSync: RESET_HOV_TARGET_DERIVED_SYNC,
    runtimeEffects: {
      presentation: "nHov-branch-swap",
      outsideResetSerial: "preserve",
    },
    nHovGrant: {
      branchId: input.branchId,
      selectedThemeHue: input.selectedThemeHue,
      nextCount: input.nextCount,
      nextBranchExperienceByBranch: input.nextBranchExperienceByBranch,
      nextEffectBoundaryCaps: input.nextEffectBoundaryCaps,
      nextContinuousEffectProgressByBranch:
        input.nextContinuousEffectProgressByBranch ?? {},
    },
  };
}

export function resolveOHovResetPlan(
  input: ResolveOHovResetPlanInput,
): OHovResetPlan {
  return {
    layer: "oHov",
    family: "outside",
    progress: {
      hovResource: "reset-to-zero",
      hiddenHovGainBonus: "reset-to-zero",
      purchasedHovGainUpgrades: "reset-to-zero",
      hovUpgradeCost: "reset-to-base",
      timUpgradeCost: "preserve",
      hovTargetGrid: "reset-to-default",
      hovGridThemeHue: "clear",
      mHovState: "reset-to-default",
      nHovState: "reset-to-default",
      secretProgressTimers: "reset-to-zero",
      hovAmplifier: "reset-gauge-and-ap-preserve-enhancement",
    },
    preserve: {
      ...COMMON_PRESERVE_SCOPE,
      oHovState: "grant",
    },
    derivedSync: RESET_HOV_TARGET_DERIVED_SYNC,
    runtimeEffects: {
      presentation: "outside-reset-burst",
      outsideResetSerial: "advance",
    },
    oHovGrant: {
      countDelta: 1,
      powerGain: input.powerGain,
      hovCoreGain: input.hovCoreGain,
      nextRunRecords: advanceOHovRunRecords(
        input.currentOHovState.records,
        input.nowMs,
        input.currentRunGameElapsedMs,
      ),
    },
  };
}
